import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Ship } from "lucide-react";

export const LEGAL_EMAIL = "recjetmedia@gmail.com";
export const EFFECTIVE_DATE = "October 23, 2026";

export function LegalLinks({ className = "", testPrefix = "footer" }) {
  return (
    <div className={`flex items-center gap-4 text-sm ${className}`}>
      <Link to="/privacy-policy" className="hover:text-gold hover:underline transition-colors" data-testid={`${testPrefix}-privacy-link`}>Privacy Policy</Link>
      <Link to="/terms-of-service" className="hover:text-gold hover:underline transition-colors" data-testid={`${testPrefix}-terms-link`}>Terms of Service</Link>
    </div>
  );
}

export function Confirm({ children }) {
  return <span className="bg-amber-100 text-amber-900 px-1 rounded font-mono text-[0.85em]" data-testid="legal-confirm-placeholder">[CONFIRM: {children}]</span>;
}

function Block({ item }) {
  if (Array.isArray(item)) {
    return <ul className="list-disc pl-6 space-y-1.5">{item.map((li, i) => <li key={i}>{li}</li>)}</ul>;
  }
  return <p>{item}</p>;
}

export default function LegalLayout({ title, slug, intro, sections }) {
  useEffect(() => {
    document.title = `${title} | Marine Ad AI by RecJet Technologies`;
    window.scrollTo(0, 0);
  }, [title]);
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid={`${slug}-page`}>
      <header className="navy-gradient border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid={`${slug}-home-link`}>
            <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center"><Ship className="w-5 h-5 text-navy" /></div>
            <div className="leading-tight">
              <div className="text-lg font-bold text-white tracking-tight">Marine Ad AI</div>
              <div className="text-[10px] uppercase tracking-widest text-gold/80 -mt-0.5">by RecJet Technologies</div>
            </div>
          </Link>
          <Link to="/login" className="text-gold font-semibold text-sm hover:underline" data-testid={`${slug}-login-link`}>Dealer Login</Link>
        </div>
      </header>
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-12 fade-up">
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-navy mb-3" data-testid={`${slug}-title`}>{title}</h1>
        <p className="text-sm text-muted-foreground mb-8" data-testid={`${slug}-effective-date`}>Effective date: {EFFECTIVE_DATE}</p>
        <div className="space-y-4 text-slate-700 leading-relaxed mb-10">{intro.map((p, i) => <Block key={i} item={p} />)}</div>
        <div className="space-y-10">
          {sections.map((s, i) => (
            <section key={s.h} data-testid={`${slug}-section-${i + 1}`}>
              <h2 className="text-lg font-bold text-navy mb-3">{i + 1}. {s.h}</h2>
              <div className="space-y-3 text-slate-700 leading-relaxed text-sm sm:text-base">{s.body.map((b, j) => <Block key={j} item={b} />)}</div>
            </section>
          ))}
        </div>
      </main>
      <footer className="bg-navy-surface text-slate-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm">Marine Ad AI is a software platform of RecJet Technologies.</span>
          <LegalLinks testPrefix={`${slug}-footer`} />
        </div>
      </footer>
    </div>
  );
}
